/**
 * EXPORT — one entry point, three formats, four books.
 *
 * This replaces `analytics-export.ts`, `handleExportToPDF`, `handleExportToXLS`,
 * `modifyAllFilteredDataForExcel`, `modifyPieDataForExcel`, `exportPDF` and
 * `exportToXLS`.
 *
 * ── WHAT WAS ACTUALLY BROKEN ──
 *
 *   · The reports PDF required FOUR DOM ids to exist and returned silently
 *     otherwise. Two of them are rendered only for Visitors and Building
 *     Activities, so Parcels and Residents produced NO FILE, NO ERROR AND NO
 *     MESSAGE — and neither did any book before a filter had been run.
 *   · The XLSX always wrote the VISITOR approval aggregation into Sheet2,
 *     whatever the book, so Parcels and Residents got a sheet of zeros.
 *   · Every sheet was labelled `VisitorType` / `NumberOfPeople`, including the
 *     Building Activities pie.
 *   · Filenames were constants — `chart.pdf`, `chart_data.xlsx` — carrying
 *     neither the scope, the range nor a timestamp.
 *   · And the server's `X-Report-Truncated` header was never read, so a
 *     truncated result exported as if it were complete.
 *
 * Every one of those is a property this module is tested on.
 *
 * ── IT DOES NOT BLOCK ──
 *
 * The row set is fetched a page at a time with a yield between pages, so a
 * five-thousand-row export leaves the screen usable and reports progress. A
 * failure surfaces the stage that failed rather than downloading nothing.
 */
import { AnalyticsBatchResult } from '../../../../../api/analytics-client';
import { DashboardData, RECORDS_PAGE_SIZE } from '../data/dashboard-data';
import { RecordRow, RecordsClock, fetchRecords } from '../data/records-api';
import { DashboardFilter, FilterMeta } from '../state/filter-model';
import { SLICES_FOR_TAB, SliceName } from '../state/filter-slices';
import { downloadBlob } from '../charts/chart-png';
import { ExportModel, buildExportModel } from './export-rows';
import { toCsv, csvBlob } from './export-csv';
/* xlsx AND jspdf ARE LOADED ON DEMAND, NOT AT IMPORT TIME.
   Between them they are the two largest dependencies this screen can reach, and
   a reader who never presses Export must not pay for either — a static import
   here puts both in the same chunk as the dashboard itself. `import()` also
   keeps jspdf's canvas probe out of every render, which is what it was doing in
   jsdom before this. */
const xlsxModule = () => import('./export-xlsx');
const pdfModule = () => import('./export-pdf');

export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface ExportContext {
  filter: DashboardFilter;
  filterMeta?: FilterMeta;
  data: DashboardData;
  results: Record<string, AnalyticsBatchResult | undefined>;
  columns: string[] | null;
  /** A single panel's own CSV, from the hover action. */
  panelId?: string;
  onProgress?: (stage: string, pct: number) => void;
  signal?: AbortSignal;
}

/**
 * ONLY THE SLICES THIS TAB LOADS. `data` holds every slice the screen has ever
 * fetched, and a tab only refreshes its own (SLICES_FOR_TAB). Exporting from
 * Records after visiting Overview therefore wrote Overview's chart numbers —
 * computed under the PREVIOUS filter — beneath a cover naming the new one
 * ("Building: Koktower" over a league of all four buildings; exports-filters
 * suite, 2026-09-23). A slice the tab does not own is exported as absent, and
 * the chart results derived from `batch` go with it.
 */
export const scopeToTab = (
  ctx: Pick<ExportContext, 'filter' | 'data' | 'results'>
): Pick<ExportContext, 'data' | 'results'> => {
  const owned = new Set<SliceName>(
    SLICES_FOR_TAB[ctx.filter.tab] || SLICES_FOR_TAB.overview
  );
  const data = { ...ctx.data } as DashboardData;
  for (const key of Object.keys(data) as SliceName[]) {
    if (!owned.has(key)) {
      data[key] = { ...data[key], data: undefined } as never;
    }
  }

  return { data, results: owned.has('batch') ? ctx.results : {} };
};

/** Give the browser a frame. A tight fetch loop with no yield is a frozen tab. */
const breathe = () => new Promise(resolve => setTimeout(resolve, 0));

/**
 * EVERY MATCHING ROW, not just the page on screen.
 *
 * "What is on screen under the current filters" means the filtered RESULT, and a
 * manager exporting a report means the report. The page size is the server's,
 * the ceiling is the server's, and the ceiling is announced in the file rather
 * than hidden.
 */
const fetchAllRows = async (
  filter: DashboardFilter,
  onProgress: ((stage: string, pct: number) => void) | undefined,
  signal: AbortSignal | undefined
): Promise<{
  rows: RecordRow[];
  truncated: boolean;
  rowLimit: number | null;
  clock?: RecordsClock;
}> => {
  const rows: RecordRow[] = [];
  let truncated = false;
  let rowLimit: number | null = null;
  // The clock the rows are on. Every page of one export is answered on the same
  // one (same reader, same header), so the first page's stands for the file.
  let clock: RecordsClock | undefined;
  // A hard stop, so a server that always answers a full page cannot spin here
  // for ever. 40 pages of 50 is 2,000 rows — well inside the server's own
  // ceiling, and the banner says when it was hit.
  const MAX_PAGES = 40;

  for (let page = 0; page < MAX_PAGES; page++) {
    if (signal?.aborted) throw new Error('Export cancelled.');
    onProgress?.('Collecting rows', Math.min(60, (page + 1) * 6));
    const answer = await fetchRecords({ ...filter, page }, RECORDS_PAGE_SIZE, { signal });
    rows.push(...answer.rows);
    if (!clock && answer.clock) clock = answer.clock;
    if (answer.truncated) {
      truncated = true;
      rowLimit = answer.rowLimit;
    }
    if (answer.rows.length < RECORDS_PAGE_SIZE) break;
    if (page === MAX_PAGES - 1) {
      truncated = true;
      rowLimit = rows.length;
    }
    await breathe();
  }

  return { rows, truncated, rowLimit, clock };
};

export const exportView = async (
  format: ExportFormat,
  ctx: ExportContext
): Promise<void> => {
  const { filter, filterMeta, columns, panelId, onProgress, signal } = ctx;
  const { data, results } = scopeToTab(ctx);

  // A per-panel CSV needs no rows at all — it is that panel's own numbers.
  const needsRows = !panelId;
  const rowSet = needsRows
    ? await fetchAllRows(filter, onProgress, signal)
    : {
        rows: [] as RecordRow[],
        truncated: false,
        rowLimit: null,
        clock: undefined as RecordsClock | undefined,
      };

  onProgress?.('Building the file', 70);

  const model: ExportModel = buildExportModel({
    filter,
    filterMeta,
    data,
    results,
    rows: rowSet.rows,
    truncated: rowSet.truncated,
    rowLimit: rowSet.rowLimit,
    clock: rowSet.clock,
    columns,
    panelId,
  });

  if (signal?.aborted) throw new Error('Export cancelled.');

  if (format === 'csv') {
    downloadBlob(`${model.filename}.csv`, csvBlob(toCsv(model)));
    onProgress?.('Done', 100);

    return;
  }

  if (format === 'xlsx') {
    const { buildWorkbook } = await xlsxModule();
    const blob = await buildWorkbook(model);
    downloadBlob(`${model.filename}.xlsx`, blob);
    onProgress?.('Done', 100);

    return;
  }

  onProgress?.('Rendering the charts', 80);
  const { buildPdf } = await pdfModule();
  const blob = await buildPdf(model, onProgress);
  downloadBlob(`${model.filename}.pdf`, blob);
  onProgress?.('Done', 100);
};
